import fs from 'node:fs/promises'
import path from 'node:path'
import { cwd } from 'node:process'
import { confirm } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'
import YAML from 'yaml'

import { bundle } from '@scalar/json-magic/bundle'
import { fetchUrls, readFiles } from '@scalar/json-magic/bundle/plugins/node'

import { isUrl, isYamlFileName } from '@/domains/document/helpers'
import { output } from '@/helpers/std-out'
import { measureTime } from '@/helpers/time'

export const DEFAULT_FETCH_CONCURRENCY = 5

export function BundleCommand() {
  const cmd = new Command('bundle')

  cmd.description(
    'Bundle an OpenAPI specification by resolving all references and external dependencies',
  )
  cmd.argument('[file|url]', 'Path to OpenAPI file or URL to bundle')
  cmd.option('-o, --output <file>', 'Path to save the bundled output file')
  cmd.option('--treeShake', 'Remove unused components from the bundled output')
  cmd.option(
    '--urlMap',
    'Generate a map of resolved URLs in the bundled output',
  )
  cmd.option(
    '--fetchLimit <limit>',
    'Maximum number of URLs to fetch during bundling at the same time',
  )
  cmd.action(
    async (
      input: string,
      {
        output: outputFilePath,
        treeShake,
        urlMap,
        fetchLimit,
      }: {
        output: string
        treeShake: boolean
        urlMap: boolean
        fetchLimit: number
      },
    ) => {
      if (!input) {
        return output
          .error()
          .title('Missing input')
          .message('Please provide a file path or URL to bundle')
          .exit('error')
      }

      const isUrlInput = isUrl(input)

      if (!outputFilePath) {
        if (isUrlInput) {
          return output
            .error()
            .message(
              'Please provide an output file using the -o flag when bundling from a URL.',
            )
            .exit('error')
        }

        const shouldContinue = await confirm({
          message:
            'No output file specified - this will overwrite the input file. Continue?',
          initialValue: false,
        })

        if (shouldContinue !== true) {
          return output
            .error()
            .message('Operation cancelled by user')
            .exit('error')
        }
      }

      const outputFileName = outputFilePath ?? input

      const isYaml = isYamlFileName(outputFileName)

      const resolvedInput = isUrlInput ? input : path.resolve(cwd(), input)

      // Bundle input file
      const { result, executionTimeMs } = await measureTime(() =>
        bundle(resolvedInput, {
          plugins: [
            readFiles(),
            fetchUrls({
              limit: fetchLimit ?? DEFAULT_FETCH_CONCURRENCY,
            }),
          ],
          treeShake: !!treeShake,
          urlMap: !!urlMap,
        }),
      )

      // Write the bundle output back on the filesystem
      await fs.writeFile(
        outputFilePath ?? input,
        isYaml ? YAML.stringify(result) : JSON.stringify(result),
        'utf8',
      )

      output
        .info()
        .message(
          `${as.green('📦 OpenAPI Schema bundled')} in ${as.white(`${as.bold(`${Math.round(executionTimeMs)}`)} ms`)}`,
        )
        .print()
    },
  )

  return cmd
}
